import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, Input, signal, computed, OnInit, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { TranslateLangPipe } from '../../pipes/translate-lang.pipe';
import {
  CriteriaSelectorComponent,
  CriteriaItem,
} from '../criteria-selector/criteria-selector.component';
import { UserAssetsService, LanguageService, OrganisationFilters, ProjectFilters, DynamicTranslationService } from '@gs-2.0/core';

/**
 * Filter criteria for catalog components
 */
export interface CatalogFilters {
  sort: CriteriaItem[];
  address: CriteriaItem[];
  region: CriteriaItem[];
  country: CriteriaItem[];
  cause: CriteriaItem[];
  category: CriteriaItem[];
  champions: CriteriaItem[];
}

/**
 * Synthetic filter id for the "Global" option shown at the top of the country
 * section. Selecting it drives the `globalInfluence` flag on the search payload
 * rather than filtering by a specific country.
 */
export const GLOBAL_INFLUENCE_FILTER_ID = 'GLOBAL_INFLUENCE';

/**
 * Catalog filters component - reusable for any catalog (charities, projects, etc.)
 * Based on Figma design: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1-4071
 */
@Component({
  selector: 'app-catalog-filters',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TranslateLangPipe,
    CriteriaSelectorComponent,
  ],
  templateUrl: './catalog-filters.component.html',
  styleUrls: ['./catalog-filters.component.scss'],
})
export class CatalogFiltersComponent implements OnInit {
  private userAssetsService = inject(UserAssetsService);
  private languageService = inject(LanguageService);
  private translationService = inject(DynamicTranslationService);

  /**
   * Filter type - determines which API endpoint to call
   * Defaults to 'ORGANISATION' for backward compatibility
   */
  @Input() filterType: 'ORGANISATION' | 'PROJECT' = 'ORGANISATION';

  /**
   * Default sort ID to apply when no sort is currently selected (e.g. from global config)
   */
  @Input() initialSortId: string | null = null;

  /**
   * Filter updates from API response - repopulates filters when changed
   */
  @Input() set filterUpdates(filters: OrganisationFilters | ProjectFilters | null) {
    if (filters) {
      this.updateFiltersFromData(filters);
    }
  }

  /**
   * Emits when filters change
   */
  @Output() filtersChanged = new EventEmitter<CatalogFilters>();

  /**
   * Loading state
   */
  isLoading = signal<boolean>(false);

  /**
   * Sort criteria items
   */
  sortItems = signal<CriteriaItem[]>([]);

  /**
   * Address filter items (ORGANISATION only). Keyed by country name (uuid is null).
   */
  addressItems = signal<CriteriaItem[]>([]);

  /**
   * Region filter items
   */
  regionItems = signal<CriteriaItem[]>([]);

  /**
   * Country filter items
   */
  countryItems = signal<CriteriaItem[]>([]);

  /**
   * Cause filter items
   */
  causeItems = signal<CriteriaItem[]>([]);

  /**
   * Category filter items
   */
  categoryItems = signal<CriteriaItem[]>([]);

  /**
   * Champions filter items
   */
  championsItems = signal<CriteriaItem[]>([]);

  /**
   * Computed signals to determine if filters have data
   */
  hasSortItems = computed(() => this.sortItems().length > 0);
  hasAddressItems = computed(() => this.addressItems().length > 0);
  hasRegionItems = computed(() => this.regionItems().length > 0);
  hasCountryItems = computed(() => this.countryItems().length > 0);
  hasCauseItems = computed(() => this.causeItems().length > 0);
  hasCategoryItems = computed(() => this.categoryItems().length > 0);
  hasChampionsItems = computed(() => this.championsItems().length > 0);

  /**
   * Mapping for sort option values to display labels
   */
  private readonly sortOptionsMapping: Record<string, string> = {
    ORGANISATION_NAME: 'charity_catalog.sort.organization_name',
    PROJECT_NAME: 'charity_catalog.sort.project_name',
    DATE_POSTED: 'charity_catalog.sort.posted_date',
    SCORE: 'charity_catalog.sort.featured',
  };

  ngOnInit(): void {
    this.loadFilters();
  }

  /**
   * Load filters from API based on filterType
   */
  private loadFilters(): void {
    this.isLoading.set(true);
    const currentLanguage = this.languageService.getCurrentLanguage();
    const languageCode = this.mapLanguageCode(currentLanguage.code);

    const filtersObservable = this.filterType === 'PROJECT'
      ? this.userAssetsService.getProjectFilters()
      : this.userAssetsService.getOrganisationFilters();

    filtersObservable.subscribe({
      next: (response) => {
        this.updateFiltersFromData(response.response);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error(`Error loading ${this.filterType.toLowerCase()} filters:`, error);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Update filters from API data while preserving selected states
   */
  private updateFiltersFromData(filters: OrganisationFilters | ProjectFilters): void {
    // Store currently selected IDs for each filter type
    const selectedSortIds = new Set(this.sortItems().filter(item => item.selected).map(item => item.id));
    const selectedAddressIds = new Set(this.addressItems().filter(item => item.selected).map(item => item.id));
    const selectedRegionIds = new Set(this.regionItems().filter(item => item.selected).map(item => item.id));
    const selectedCountryIds = new Set(this.countryItems().filter(item => item.selected).map(item => item.id));
    const selectedCauseIds = new Set(this.causeItems().filter(item => item.selected).map(item => item.id));
    const selectedCategoryIds = new Set(this.categoryItems().filter(item => item.selected).map(item => item.id));
    const selectedChampionIds = new Set(this.championsItems().filter(item => item.selected).map(item => item.id));

    // Transform sort options to CriteriaItem[] and preserve selections (only if not null).
    // When no sort is currently selected, fall back to the initialSortId from global config.
    if (filters.sortOptions) {
      const effectiveSortIds = selectedSortIds.size > 0
        ? selectedSortIds
        : (this.initialSortId ? new Set([this.initialSortId]) : new Set<string>());
      this.sortItems.set(
        filters.sortOptions.map((option) => ({
          id: option,
          label: this.sortOptionsMapping[option] ? this.translationService.instant(this.sortOptionsMapping[option]) : option,
          selected: effectiveSortIds.has(option),
        }))
      );
    }

    // Transform addresses to CriteriaItem[] (ORGANISATION only). Address options are
    // keyed by `name` (uuid is null), so the option name is used as the item id/value.
    const addresses = this.filterType === 'ORGANISATION'
      ? (filters as OrganisationFilters).addresses || []
      : [];
    this.addressItems.set(
      addresses.map((address) => ({
        id: address.name,
        label: address.name,
        selected: selectedAddressIds.has(address.name),
      }))
    );

    // Transform regions to CriteriaItem[] and preserve selections (always apply)
    this.regionItems.set(
      (filters.regions || []).map((region) => ({
        id: region.uuid,
        label: region.name,
        selected: selectedRegionIds.has(region.uuid),
      }))
    );

    // Transform countries to CriteriaItem[] and preserve selections (always apply).
    // Prepend a synthetic "Global" option at the top of the country section when
    // there are countries to filter by.
    const countryOptions = (filters.countries || []).map((country) => ({
      id: country.uuid,
      label: country.name,
      selected: selectedCountryIds.has(country.uuid),
    }));
    const showGlobalOption = this.filterType === 'ORGANISATION' && countryOptions.length > 0;
    this.countryItems.set(
      showGlobalOption
        ? [
            {
              id: GLOBAL_INFLUENCE_FILTER_ID,
              label: 'charity_catalog.filter.global',
              selected: selectedCountryIds.has(GLOBAL_INFLUENCE_FILTER_ID),
            },
            ...countryOptions,
          ]
        : countryOptions
    );

    // Transform causes to CriteriaItem[] and preserve selections (always apply)
    this.causeItems.set(
      (filters.causes || []).map((cause) => ({
        id: cause.uuid,
        label: cause.name,
        selected: selectedCauseIds.has(cause.uuid),
      }))
    );

    // Transform categories to CriteriaItem[] and preserve selections (always apply)
    this.categoryItems.set(
      (filters.categories || []).map((category) => ({
        id: category.uuid,
        label: category.name,
        selected: selectedCategoryIds.has(category.uuid),
      }))
    );

    // Transform champions to CriteriaItem[] and preserve selections (always apply)
    this.championsItems.set(
      (filters.champions || []).map((champion) => ({
        id: champion.uuid,
        label: champion.name,
        selected: selectedChampionIds.has(champion.uuid),
      }))
    );
  }

  /**
   * Map 2-letter language code to 3-letter code for API
   */
  private mapLanguageCode(code: string): string {
    const mapping: Record<string, string> = {
      en: 'eng',
      de: 'deu',
      es: 'spa',
      fr: 'fra',
    };
    return mapping[code] || 'eng';
  }

  /**
   * Handle sort selection change
   */
  onSortChanged(items: CriteriaItem[]): void {
    this.sortItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle address filter selection change
   */
  onAddressChanged(items: CriteriaItem[]): void {
    this.addressItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle region filter selection change
   */
  onRegionChanged(items: CriteriaItem[]): void {
    this.regionItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle country filter selection change
   */
  onCountryChanged(items: CriteriaItem[]): void {
    this.countryItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle cause filter selection change
   */
  onCauseChanged(items: CriteriaItem[]): void {
    this.causeItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle category filter selection change
   */
  onCategoryChanged(items: CriteriaItem[]): void {
    this.categoryItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Handle champions filter selection change
   */
  onChampionsChanged(items: CriteriaItem[]): void {
    this.championsItems.set(items);
    this.emitFiltersChanged();
  }

  /**
   * Clear all filters
   */
  clearAllFilters(): void {
    this.sortItems.set(this.sortItems().map(item => ({ ...item, selected: false })));
    this.addressItems.set(this.addressItems().map(item => ({ ...item, selected: false })));
    this.regionItems.set(this.regionItems().map(item => ({ ...item, selected: false })));
    this.countryItems.set(this.countryItems().map(item => ({ ...item, selected: false })));
    this.causeItems.set(this.causeItems().map(item => ({ ...item, selected: false })));
    this.categoryItems.set(this.categoryItems().map(item => ({ ...item, selected: false })));
    this.championsItems.set(this.championsItems().map(item => ({ ...item, selected: false })));
    this.emitFiltersChanged();
  }

  /**
   * Emit filters changed event
   */
  private emitFiltersChanged(): void {
    this.filtersChanged.emit({
      sort: this.sortItems(),
      address: this.addressItems(),
      region: this.regionItems(),
      country: this.countryItems(),
      cause: this.causeItems(),
      category: this.categoryItems(),
      champions: this.championsItems(),
    });
  }
}
